// The rep picker at the top, the tabs, the search box, the Filters menu and the chips for filters
// in use.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Opportunity } from '../api/types.ts';
import {
  REASON_LABELS, REASONS, TAB_LABELS, TABS, activeFilterCount, closeDate, createdDate, periodLabel,
  periodOptions, switchTab, tabHasStages,
} from '../board/view.ts';
import type { Filters, Funnel, Period, PeriodOptions, Reason, RepOption, Tab } from '../board/view.ts';

// --- Rep -------------------------------------------------------------------------------------

export function RepPicker({
  filters, reps, selectedName, onChange,
}: {
  filters: Filters;
  // Every rep with a row on the tab, by name. Only the rep: a CS owner or opportunity owner isn't listed (GRO-241).
  reps: RepOption[];
  // The picked person's name, from anywhere in the payload.
  selectedName: string | null;
  onChange: (filters: Filters) => void;
}) {
  // A person picked on another tab stays selectable here, at 0, rather than silently vanishing.
  const selectedMissing = filters.rep_id !== null && !reps.some(rep => rep.user_id === filters.rep_id);
  return (
    <label className={'rep-picker' + (filters.rep_id ? ' is-active' : '')}>
      <span className="rep-picker-label">Rep</span>
      <select
        value={filters.rep_id ?? ''}
        onChange={event => onChange({ ...filters, rep_id: event.target.value || null })}
      >
        <option value="">Everyone</option>
        {selectedMissing ? <option value={filters.rep_id ?? ''}>{selectedName ?? 'Selected rep'} (0)</option> : null}
        {reps.map(rep => (
          <option key={rep.user_id} value={rep.user_id}>{rep.name} ({rep.row_count})</option>
        ))}
      </select>
    </label>
  );
}

// --- Tabs ------------------------------------------------------------------------------------

export function Tabs({
  filters, counts, onChange,
}: {
  filters: Filters;
  counts: Record<Tab, number>;
  onChange: (filters: Filters) => void;
}) {
  return (
    <div className="tabs" role="tablist" aria-label="Which opportunities">
      {TABS.map(tab => (
        <button
          key={tab}
          type="button"
          role="tab"
          aria-selected={filters.tab === tab}
          onClick={() => onChange(switchTab(filters, tab))}
        >
          {TAB_LABELS[tab]}
          <span className="tab-count num">{counts[tab]}</span>
        </button>
      ))}
    </div>
  );
}

// --- Search ----------------------------------------------------------------------------------

export function SearchBox({ filters, onChange }: { filters: Filters; onChange: (filters: Filters) => void }) {
  return (
    <label className="search">
      <span className="visually-hidden">Search accounts and opportunities</span>
      <svg aria-hidden="true" viewBox="0 0 16 16" width="14" height="14">
        <circle cx="7" cy="7" r="4.75" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M10.5 10.5 14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      <input
        type="search"
        placeholder="Search accounts or opportunities"
        value={filters.search}
        onChange={event => onChange({ ...filters, search: event.target.value })}
      />
    </label>
  );
}

// --- The Filters menu ------------------------------------------------------------------------

function toggleReason(reasons: Reason[], reason: Reason): Reason[] {
  return reasons.includes(reason) ? reasons.filter(item => item !== reason) : [...reasons, reason];
}

function PeriodSelect({
  label, value, options, onChange,
}: {
  label: string;
  value: Period | null;
  options: PeriodOptions;
  onChange: (value: Period | null) => void;
}) {
  return (
    <label className="menu-field">
      <span className="menu-label">{label}</span>
      <select value={value ?? ''} onChange={event => onChange(event.target.value || null)}>
        <option value="">Any time</option>
        <optgroup label="Quarter">
          {options.quarters.map(period => <option key={period} value={period}>{periodLabel(period)}</option>)}
        </optgroup>
        <optgroup label="Month">
          {options.months.map(period => <option key={period} value={period}>{periodLabel(period)}</option>)}
        </optgroup>
      </select>
    </label>
  );
}

// Closes on Escape or a click outside it, as a menu should.
function useDismiss(open: boolean, close: () => void) {
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (container.current && !container.current.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => { if (event.key === 'Escape') close(); };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);
  return container;
}

export function FilterMenu({
  filters, funnel, tabRows, onChange, onClear,
}: {
  filters: Filters;
  funnel: Funnel;
  // The tab's rows before any table filter, for the months and quarters on offer.
  tabRows: Opportunity[];
  onChange: (filters: Filters) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const container = useDismiss(open, close);
  const isOpenTab = tabHasStages(filters.tab);
  const count = activeFilterCount(filters);

  return (
    <div className="filter-menu" ref={container}>
      <button
        type="button"
        className={'button' + (count > 0 ? ' is-active' : '')}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        Filters
        {count > 0 ? <span className="button-count num">{count}</span> : null}
        <span aria-hidden="true" className="caret">▾</span>
      </button>

      {open ? (
        <div className="menu" role="dialog" aria-label="Filters">
          {isOpenTab ? (
            <label className="menu-field">
              <span className="menu-label">Stage</span>
              <select
                value={filters.stage ?? ''}
                onChange={event => onChange({ ...filters, stage: event.target.value ? Number(event.target.value) : null })}
              >
                <option value="">Every stage</option>
                {funnel.stages.map(stage => (
                  <option key={stage.stage_number} value={stage.stage_number ?? ''}>{stage.label}</option>
                ))}
              </select>
            </label>
          ) : null}

          {isOpenTab ? (
            <fieldset className="menu-field">
              <legend className="menu-label">Needs attention</legend>
              {REASONS.map(reason => (
                <label key={reason} className="menu-check">
                  <input
                    type="checkbox"
                    checked={filters.reasons.includes(reason)}
                    onChange={() => onChange({ ...filters, reasons: toggleReason(filters.reasons, reason) })}
                  />
                  {REASON_LABELS[reason]}
                </label>
              ))}
            </fieldset>
          ) : null}

          <PeriodSelect
            label="Opened"
            value={filters.created}
            options={periodOptions(tabRows, createdDate)}
            onChange={created => onChange({ ...filters, created })}
          />
          <PeriodSelect
            label="Close date"
            value={filters.closing}
            options={periodOptions(tabRows, closeDate)}
            onChange={closing => onChange({ ...filters, closing })}
          />

          <div className="menu-foot">
            <button type="button" className="link-button" onClick={onClear} disabled={count === 0}>Clear all</button>
            <button type="button" className="button" onClick={close}>Done</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// --- The chips for filters in use ------------------------------------------------------------

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <button type="button" className="chip" onClick={onRemove} aria-label={`Remove filter: ${label}`}>
      {label}<span aria-hidden="true" className="chip-x">×</span>
    </button>
  );
}

export function ActiveFilters({
  filters, funnel, onChange, onClear,
}: {
  filters: Filters;
  funnel: Funnel;
  onChange: (filters: Filters) => void;
  onClear: () => void;
}) {
  const isOpenTab = tabHasStages(filters.tab);
  if (activeFilterCount(filters) === 0) return null;
  const stageLabel = funnel.stages.find(stage => stage.stage_number === filters.stage)?.label;

  return (
    <div className="active-filters" aria-label="Filters in use">
      {isOpenTab && filters.stage !== null ? (
        <Chip label={stageLabel ?? `Stage ${filters.stage}`} onRemove={() => onChange({ ...filters, stage: null })} />
      ) : null}
      {isOpenTab ? filters.reasons.map(reason => (
        <Chip
          key={reason}
          label={REASON_LABELS[reason]}
          onRemove={() => onChange({ ...filters, reasons: filters.reasons.filter(item => item !== reason) })}
        />
      )) : null}
      {filters.created ? (
        <Chip label={`Opened ${periodLabel(filters.created)}`} onRemove={() => onChange({ ...filters, created: null })} />
      ) : null}
      {filters.closing ? (
        <Chip label={`Closing ${periodLabel(filters.closing)}`} onRemove={() => onChange({ ...filters, closing: null })} />
      ) : null}
      <button type="button" className="link-button" onClick={onClear}>Clear all</button>
    </div>
  );
}
